/**
 * Guest-side connection quality probe for initial ShadowCam tier selection.
 *
 * The Network Information API cannot answer "is this link questionable?", and worse, it cannot
 * be asked the question on half the fleet:
 *  - Safari (every iPhone/iPad) does not implement `navigator.connection` at all, so a
 *    NetInfo-only predicate can never return true there.
 *  - Chromium reports `effectiveType: '4g'` for anything better than ~700 kb/s with an
 *    HTTP RTT under ~273 ms, and rounds `downlink` to the nearest 25 kb/s capped at 10,
 *    so a one-bar cellular link that still trickles a couple of Mb/s reads as "4g".
 *
 * PLATFORM PARITY — the verdict path therefore does NOT consult NetInfo. An iPhone and an
 * Android phone on the same real network used to be seated on different quality tiers purely
 * because one of them had an API the other does not, which is an accident of vendor support
 * rather than a property of the link. Every input the verdict reads comes from timings the
 * browser already collected while loading the page (Navigation/Resource Timing): identical
 * measurements on both platforms, free at connect time, and available on Safari.
 *
 * The NetInfo reading is still *sampled* and still logged, because it is useful evidence when
 * reading a trace, and `isQuestionableConnection` is still exported for the callers that want
 * the raw predicate. It just no longer decides anything.
 */

/** Network Information API reading (all fields optional — the API is not universal). */
export type NetworkConnectionHint = {
  /** 'slow-2g' | '2g' | '3g' | '4g' | ... */
  effectiveType?: string;
  /** Estimated downlink in Mb/s. */
  downlink?: number;
  /** Estimated HTTP round-trip in ms. */
  rtt?: number;
};

/**
 * A mobile guest streams the middle tier at 2–8 Mb/s (MOBILE_START/MAX_BITRATE_BPS), so a link
 * the browser itself estimates below this cannot hold 480p60 and belongs on the 360p tier.
 */
export const QUESTIONABLE_DOWNLINK_MBPS = 3;

/**
 * Chromium's `connection.rtt` is an HTTP RTT estimate rounded to 25 ms, so it runs higher than
 * the candidate-pair RTT the host recovers on. This sits above typical Wi-Fi/good-LTE readings
 * (50–150 ms) and below Chromium's own `effectiveType: '3g'` cutoff (~273 ms), which is the gap
 * where a weak-but-usable cellular link lives.
 */
export const QUESTIONABLE_NETINFO_RTT_MS = 250;

/** Effective types that are questionable regardless of the numeric estimates. */
const QUESTIONABLE_EFFECTIVE_TYPES = new Set(['slow-2g', '2g', '3g']);

/**
 * Measured page-load throughput below this is questionable. Deliberately close to the tier's
 * own bitrate need rather than to a "slow web page" threshold — we are asking whether the link
 * can carry a game stream, not whether the site felt fast.
 */
export const MEASURED_QUESTIONABLE_THROUGHPUT_MBPS = 2.5;

/**
 * Measured time-to-first-byte above this is questionable. Set well above the CDN/cold-start
 * noise floor so it only fires on links that are genuinely latency-bound.
 */
export const MEASURED_QUESTIONABLE_TTFB_MS = 600;

/**
 * Resource timing entries smaller than this are dominated by round-trip latency rather than
 * bandwidth, so their implied throughput says nothing about link capacity.
 */
export const MIN_THROUGHPUT_SAMPLE_BYTES = 40_000;

/**
 * PRODUCT DECISION — currently OFF.
 *
 * When true, a mobile guest whose connection cannot be characterised at all (no Network
 * Information API *and* no usable page-load timings) is treated as questionable and starts on
 * the 360p tier. Turning this on makes the fail-safe direction "assume the worst on mobile";
 * leaving it off keeps the historical behaviour of starting such guests at 480p.
 */
export const TREAT_UNKNOWN_MOBILE_CONNECTION_AS_QUESTIONABLE = false;

/**
 * PRODUCT DECISION — currently ON.
 *
 * When true, a mobile guest that is reclaiming its signaling identity after losing its
 * connection reports a questionable link regardless of what the other signals say.
 *
 * Every measured input below comes from Navigation/Resource Timing, which is frozen at page
 * load. A re-sample taken after a failover therefore still describes the *original* link and
 * reports healthy — the one verdict a reclaim must never produce. Turning this off makes a
 * reclaim re-sample indistinguishable from the initial sample.
 */
export const TREAT_MOBILE_RECLAIM_AS_QUESTIONABLE = true;

/**
 * A mobile reclaim counts as "quick" — and therefore not evidence of a bad link — when the
 * whole recovery episode stayed under this long. Android Wi-Fi power-saving sleeps the radio
 * for a second or so; when the phone wakes it finds the transport gone and reclaims. That
 * reclaim is a strong witness that the link is fine (it came right back), so forcing
 * `questionable` on every reclaim permanently seats a phone that merely blinked on 360p.
 */
export const MOBILE_RECLAIM_QUICK_MS = 3_000;

/**
 * True when a reclaim sample should force `questionable` solely because the connection
 * failed. Quick recoveries are exempt — the link that came right back is not evidence of a
 * bad link, and the frozen page-load timings add no evidence either way.
 */
export function shouldForceMobileReclaimQuestionable(
  isMobile: boolean,
  recoveredWithinMs: number | undefined,
): boolean {
  if (!TREAT_MOBILE_RECLAIM_AS_QUESTIONABLE || !isMobile) return false;
  if (recoveredWithinMs === undefined) return true;
  return recoveredWithinMs >= MOBILE_RECLAIM_QUICK_MS;
}

export type ConnectionQualitySample = {
  /** null when the Network Information API is unavailable (Safari / iOS). */
  networkInformation: NetworkConnectionHint | null;
  /** Best observed page-load throughput in Mb/s, or null when nothing was measurable. */
  measuredThroughputMbps: number | null;
  /** Navigation time-to-first-byte in ms, or null when unavailable. */
  measuredTtfbMs: number | null;
};

/**
 * `'network-information'` is retained so previously stored/logged verdicts still type-check,
 * but nothing produces it any more — see the platform-parity note at the top of this file.
 */
export type ConnectionQualityReason =
  | 'network-information'
  | 'measured-throughput'
  | 'measured-ttfb'
  | 'unknown-mobile'
  | 'mobile-reclaim'
  | 'healthy';

/** Whether a sample describes a first connect or a post-drop reclaim. */
export type ConnectionSamplePhase = 'initial' | 'reclaim';

export type ConnectionQualityVerdict = {
  questionable: boolean;
  reason: ConnectionQualityReason;
  sample: ConnectionQualitySample;
};

/** Minimal shapes so the probe can be exercised without a DOM. */
export type PerformanceTimingSource = {
  getEntriesByType: (type: string) => unknown[];
};

export type ConnectionProbeEnvironment = {
  connection?: NetworkConnectionHint | null;
  performance?: PerformanceTimingSource | null;
};

/**
 * NetInfo-only predicate. Missing API or missing fields never make a link questionable.
 *
 * NOT part of `evaluateConnectionQuality`'s verdict path — see the platform-parity note at the
 * top of this file. Kept because other callers (`selectInitialQualityTier`, host-side tier
 * selection) still accept a raw `NetworkConnectionHint` and need a way to interpret one.
 */
export function isQuestionableConnection(hint?: NetworkConnectionHint | null): boolean {
  if (hint == null) return false;
  if (
    typeof hint.effectiveType === 'string' &&
    QUESTIONABLE_EFFECTIVE_TYPES.has(hint.effectiveType)
  ) {
    return true;
  }
  if (typeof hint.downlink === 'number' && hint.downlink < QUESTIONABLE_DOWNLINK_MBPS) {
    return true;
  }
  if (typeof hint.rtt === 'number' && hint.rtt >= QUESTIONABLE_NETINFO_RTT_MS) {
    return true;
  }
  return false;
}

/** Reads `navigator.connection` defensively; returns null when the API is absent. */
export function readNetworkInformation(
  nav: unknown = typeof navigator === 'undefined' ? undefined : navigator,
): NetworkConnectionHint | null {
  const connection = (nav as { connection?: NetworkConnectionHint } | undefined)?.connection;
  if (connection == null || typeof connection !== 'object') return null;
  return {
    effectiveType: typeof connection.effectiveType === 'string' ? connection.effectiveType : undefined,
    downlink: typeof connection.downlink === 'number' ? connection.downlink : undefined,
    rtt: typeof connection.rtt === 'number' ? connection.rtt : undefined,
  };
}

type ResourceTimingLike = {
  duration?: number;
  transferSize?: number;
  encodedBodySize?: number;
};

type NavigationTimingLike = {
  requestStart?: number;
  responseStart?: number;
};

/**
 * Best throughput observed while loading the page, in Mb/s.
 *
 * Takes the maximum rather than the mean: every entry is a lower bound on link capacity (slow
 * start, server think time and queuing all drag individual transfers down), so the fastest
 * qualifying transfer is the closest estimate of what the link can actually do. Cache hits and
 * cross-origin entries without Timing-Allow-Origin report `transferSize === 0` and are skipped
 * because their duration reflects no network work.
 */
export function measureLoadThroughputMbps(perf?: PerformanceTimingSource | null): number | null {
  const entries = safeEntries(perf, 'resource') as ResourceTimingLike[];
  let best: number | null = null;

  for (const entry of entries) {
    const bytes = numberOrZero(entry?.encodedBodySize);
    const transferred = numberOrZero(entry?.transferSize);
    const durationMs = numberOrZero(entry?.duration);
    if (transferred <= 0 || bytes < MIN_THROUGHPUT_SAMPLE_BYTES || durationMs <= 0) continue;

    const mbps = (bytes * 8) / (durationMs * 1000);
    if (best === null || mbps > best) best = mbps;
  }

  return best;
}

/** Navigation time-to-first-byte in ms, or null when Navigation Timing is unusable. */
export function measureNavigationTtfbMs(perf?: PerformanceTimingSource | null): number | null {
  const entries = safeEntries(perf, 'navigation') as NavigationTimingLike[];
  const entry = entries[0];
  if (!entry) return null;

  const requestStart = numberOrZero(entry.requestStart);
  const responseStart = numberOrZero(entry.responseStart);
  if (requestStart <= 0 || responseStart <= requestStart) return null;

  return responseStart - requestStart;
}

export function sampleConnectionQuality(
  env: ConnectionProbeEnvironment = {},
): ConnectionQualitySample {
  const perf =
    env.performance !== undefined
      ? env.performance
      : typeof performance === 'undefined'
        ? null
        : (performance as unknown as PerformanceTimingSource);

  return {
    networkInformation: env.connection !== undefined ? env.connection : readNetworkInformation(),
    measuredThroughputMbps: measureLoadThroughputMbps(perf),
    measuredTtfbMs: measureNavigationTtfbMs(perf),
  };
}

/**
 * Combines the *measured* signals into the single boolean the host reads.
 *
 * `sample.networkInformation` is deliberately NOT consulted. It exists on Android and not on
 * iOS, so consulting it handed the two platforms different tiers for the same real link —
 * a divergence produced by vendor API support rather than by anything about the network.
 * Precedence is: reclaim → measured throughput → measured TTFB → unknown-mobile → healthy.
 *
 * Losing the live signal costs Android some sensitivity on a genuinely poor link, and that is
 * the accepted price: the measured throughput and TTFB paths still fire on both platforms, the
 * congestion ladder still demotes bitrate in-session, and the reclaim rule below still forces
 * `questionable` the moment anything actually fails.
 */
export function evaluateConnectionQuality(
  sample: ConnectionQualitySample,
  options: {
      isMobile?: boolean;
      isReclaim?: boolean;
      /** Episode duration when the reclaim was sampled; see shouldForceMobileReclaimQuestionable. */
      recoveredWithinMs?: number;
    } = {},
): ConnectionQualityVerdict {
  if (
    options.isReclaim === true &&
    shouldForceMobileReclaimQuestionable(options.isMobile === true, options.recoveredWithinMs)
  ) {
    return { questionable: true, reason: 'mobile-reclaim', sample };
  }
  if (
    sample.measuredThroughputMbps !== null &&
    sample.measuredThroughputMbps < MEASURED_QUESTIONABLE_THROUGHPUT_MBPS
  ) {
    return { questionable: true, reason: 'measured-throughput', sample };
  }
  if (sample.measuredTtfbMs !== null && sample.measuredTtfbMs > MEASURED_QUESTIONABLE_TTFB_MS) {
    return { questionable: true, reason: 'measured-ttfb', sample };
  }

  // Only the measured inputs count towards "characterised", for the same parity reason: a
  // NetInfo reading would make an Android guest look characterised where an identical iPhone
  // did not, which is precisely the asymmetry this function no longer expresses.
  const characterised =
    sample.measuredThroughputMbps !== null || sample.measuredTtfbMs !== null;

  if (
    !characterised &&
    options.isMobile === true &&
    TREAT_UNKNOWN_MOBILE_CONNECTION_AS_QUESTIONABLE
  ) {
    return { questionable: true, reason: 'unknown-mobile', sample };
  }

  return { questionable: false, reason: 'healthy', sample };
}

export type ConnectionProbeLogger = (message: string, detail: Record<string, unknown>) => void;

export type GuestConnectionSampleOptions = {
  isMobile?: boolean;
  env?: ConnectionProbeEnvironment;
  log?: ConnectionProbeLogger | null;
};

export type GuestReclaimConnectionSampleOptions = GuestConnectionSampleOptions & {
  /** Reconnect attempt number, from GuestBeforeReconnectInfo. */
  attempt?: number;
  /**
   * How long the current recovery episode had already run when this reclaim sample was
   * taken, ms. A recovery that succeeds quickly (Android radio sleep) is not evidence of
   * a bad link; one that ran the full budget is. See shouldForceMobileReclaimQuestionable.
   */
  recoveredWithinMs?: number;
};

/**
 * Distinct per phase on purpose: the two samples produce byte-identical evidence otherwise,
 * so a trace could not show whether a hint came from the first connect or from a reclaim.
 */
const TIER_HINT_LOG_MESSAGE: Record<ConnectionSamplePhase, string> = {
  initial: '[ConnectionProbe] initial tier hint',
  reclaim: '[ConnectionProbe] reclaim tier hint',
};

/**
 * The call guests make on first connect: sample, decide, and log the evidence. Without the
 * log there is no way to tell a link that was judged healthy from one that was never
 * characterised.
 */
export function sampleGuestConnectionQuestionable(
  options: GuestConnectionSampleOptions = {},
): boolean {
  return sampleGuestQuestionable(options, 'initial');
}

/**
 * Whether this guest has already lost a connection it was supposed to keep.
 *
 * Session-scoped and deliberately shared: a guest reaches the host through several doors —
 * the in-session reclaim, a full peer rebuild, and the guest and beacon fast-start payloads
 * (which are re-sent on every WebSocket reconnect). Only the reclaim door used the recovery
 * sampler, so a mobile guest that came back through any of the others was reported healthy
 * and seated on the 480p camera instead of being stepped down to 360p.
 */
let guestConnectionFailed = false;

/**
 * Record that the live connection failed, so every later (re)build is hinted as a recovery.
 */
export function markGuestConnectionFailed(): void {
  guestConnectionFailed = true;
}

/** Forget the failure, as a fresh page load would. Primarily a test seam. */
export function resetGuestConnectionFailed(): void {
  guestConnectionFailed = false;
}

/** True once this guest has lost a connection during this page's lifetime. */
export function hasGuestConnectionFailed(): boolean {
  return guestConnectionFailed;
}

/**
 * The connection-quality hint for ANY (re)build of a guest's connection.
 *
 * Before the first failure this is the ordinary initial sample. Afterwards it is the reclaim
 * sample, because the initial one cannot describe a recovery: its inputs are Navigation and
 * Resource Timing values frozen at page load, so re-running it after a drop characterises
 * the link that already failed and reports it healthy — the one verdict a recovery must
 * never produce.
 *
 * Use this everywhere a guest announces itself. `sampleGuestConnectionQuestionable` remains
 * correct only for a genuine first connect.
 */
export function sampleGuestQuestionableForBuild(
  options: GuestReclaimConnectionSampleOptions = {},
): boolean {
  return guestConnectionFailed
    ? sampleGuestReclaimConnectionQuestionable(options)
    : sampleGuestConnectionQuestionable(options);
}

/**
 * The call guests make when reclaiming their signaling identity after a drop.
 *
 * Separate from the initial entry point because a reclaim carries information the sample
 * itself cannot: the previous connection failed. The frozen page-load timings would
 * otherwise describe the pre-drop link and report the recovered guest as healthy.
 */
export function sampleGuestReclaimConnectionQuestionable(
  options: GuestReclaimConnectionSampleOptions = {},
): boolean {
  return sampleGuestQuestionable(
      options,
      'reclaim',
      options.attempt,
      options.recoveredWithinMs,
    );
}

function sampleGuestQuestionable(
  options: GuestConnectionSampleOptions,
  phase: ConnectionSamplePhase,
  attempt?: number,
  recoveredWithinMs?: number,
): boolean {
  let verdict: ConnectionQualityVerdict;
  try {
    verdict = evaluateConnectionQuality(sampleConnectionQuality(options.env), {
      isMobile: options.isMobile,
      isReclaim: phase === 'reclaim',
      recoveredWithinMs,
    });
  } catch {
    return false;
  }

  const log =
    options.log === undefined
      ? (message: string, detail: Record<string, unknown>) => console.log(message, detail)
      : options.log;

  log?.(TIER_HINT_LOG_MESSAGE[phase], {
    phase,
    attempt: attempt ?? 'n/a',
    isMobile: options.isMobile === true,
    questionable: verdict.questionable,
    reason: verdict.reason,
    effectiveType: verdict.sample.networkInformation?.effectiveType ?? 'unavailable',
    downlinkMbps: verdict.sample.networkInformation?.downlink ?? 'unavailable',
    netInfoRttMs: verdict.sample.networkInformation?.rtt ?? 'unavailable',
    measuredThroughputMbps: verdict.sample.measuredThroughputMbps ?? 'unavailable',
    measuredTtfbMs: verdict.sample.measuredTtfbMs ?? 'unavailable',
  });

  return verdict.questionable;
}

function safeEntries(perf: PerformanceTimingSource | null | undefined, type: string): unknown[] {
  if (!perf || typeof perf.getEntriesByType !== 'function') return [];
  try {
    const entries = perf.getEntriesByType(type);
    return Array.isArray(entries) ? entries : [];
  } catch {
    return [];
  }
}

function numberOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
